import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

// Modal overlay
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  
  /* Mobile optimizations */
  @media (max-width: 768px) {
    padding: 0.5rem;
    align-items: flex-start;
    padding-top: 2rem;
  }
  
  /* Prevent scrolling on mobile */
  @supports (-webkit-overflow-scrolling: touch) {
    position: fixed;
    overflow: hidden;
  }
`;

// Modal container
const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  
  /* Mobile optimizations */
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 16px;
    margin: 0 auto;
    width: calc(100% - 1rem);
    max-width: 400px;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 12px;
    width: calc(100% - 0.5rem);
  }
  
  /* Safe area for notched devices */
  @supports (padding: max(0px)) {
    padding-left: max(1rem, env(safe-area-inset-left));
    padding-right: max(1rem, env(safe-area-inset-right));
    padding-bottom: max(1rem, env(safe-area-inset-bottom));
  }
`;

// Modal header
const ModalHeader = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const ModalSubtitle = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const InfoBox = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1.5rem;
`;

const InfoText = styled.p`
  margin: 0;
  color: #0369a1;
  font-size: 0.875rem;
  line-height: 1.5;
`;

const InfoList = styled.ul`
  margin: 0.5rem 0 0 0;
  padding-left: 1.5rem;
  color: #0369a1;
  font-size: 0.875rem;
  line-height: 1.8;
`;

const InfoListItem = styled.li`
  margin-bottom: 0.25rem;
`;

// Buttons
const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  
  /* Mobile optimizations */
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  /* Mobile optimizations */
  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
    font-size: 16px;
    border-radius: 8px;
    width: 100%;
    min-height: 48px; /* Touch target size */
  }
  
  @media (max-width: 480px) {
    padding: 0.875rem 1.25rem;
    font-size: 16px;
  }
`;

const PrimaryButton = styled(Button)`
  background: #043663;
  color: white;
  
  &:hover:not(:disabled) {
    background: #032a4f;
  }
`;

const SecondaryButton = styled(Button)`
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  
  &:hover:not(:disabled) {
    background: #e5e7eb;
  }
`;

const TeamMemberInfoModal = ({ isOpen, onClose, onGoToTeamMembers }) => {
  const navigate = useNavigate();

  // Prevent body scroll when modal is open (important for mobile)
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Prevent iOS bounce scrolling
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  const handleGoToTeamMembers = () => {
    onClose();
    if (onGoToTeamMembers) {
      onGoToTeamMembers();
    } else {
      navigate('/dashboard?section=team');
    }
  };

  const handleSkip = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>📝 Update Team Member Information</ModalTitle>
          <ModalSubtitle>
            We've added new fields to help us better organize the league
          </ModalSubtitle>
        </ModalHeader>

        <InfoBox>
          <InfoText>
            <strong>Please fill out the following information for all team members:</strong>
            <InfoList>
              <InfoListItem>Phone Number</InfoListItem>
              <InfoListItem>Date of Birth</InfoListItem>
              <InfoListItem>Sex</InfoListItem>
            </InfoList>
          </InfoText>
        </InfoBox>

        <ButtonContainer>
          <SecondaryButton
            type="button"
            onClick={handleSkip}
          >
            Skip for Now
          </SecondaryButton>
          <PrimaryButton
            type="button"
            onClick={handleGoToTeamMembers}
          >
            Go to Team Members
          </PrimaryButton>
        </ButtonContainer>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default TeamMemberInfoModal;

